import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Space, Spin } from 'antd';
import {
    AppleOutlined,
    AndroidOutlined,
    DownloadOutlined,
    ArrowRightOutlined,
    GiftOutlined,
    StarOutlined,
    RocketOutlined,
    TrophyOutlined,
    ThunderboltOutlined,
    CrownOutlined,
    FireOutlined,
    DollarOutlined,
    CheckCircleFilled,
    SafetyCertificateOutlined,
    CustomerServiceOutlined,
    SwapOutlined,
    SettingOutlined,
    CarOutlined,
    LinkOutlined,
    DisconnectOutlined,
    PhoneOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Banner, FeatureCard, AnimatedSection } from '../../components';
import { bannerService, settingsService } from '../../common/services';
import { useScrollAnimation } from '../../common/hooks';
import { FEATURES, GOOGLE_PLAY_URL, APP_STORE_URL, APP_STORE_AVAILABLE, APP_NAME } from '../../common/constants';
import Logo from '../../assets/logo-tigoon.png';
import AppScreenshot from '../../assets/app-image.jpeg';
import './HomePage.css';

// Fonction pour générer les benefits avec les valeurs dynamiques
const getDriverBenefits = (settings) => [
    {
        icon: <GiftOutlined />,
        title: `${settings.bonusPremiereRecharge} Bonus Première Recharge`,
        description: `Anciens comme nouveaux chauffeurs reçoivent ${settings.bonusPremiereRecharge} en points sur votre première recharge.`,
        highlight: settings.bonusPremiereRecharge,
        tag: 'Bienvenue'
    },
    {
        icon: <RocketOutlined />,
        title: 'Nouveau Compte TIGOON + Yandex',
        description: 'Créez un nouveau compte TIGOON qui génère un nouveau compte Yandex.',
        highlight: settings.pointsNouveau,
        tag: 'Nouveau'
    },
    {
        icon: <StarOutlined />,
        title: 'Migration vers TIGOON',
        description: 'Ancien compte Yandex qui migre vers TIGOON.',
        highlight: settings.pointsAncien,
        tag: 'Migration'
    },
    {
        icon: <DollarOutlined />,
        title: 'Cashback sur Recharges',
        description: 'À chaque rechargement effectué par un compte Yandex lié à un compte TIGOON actif.',
        highlight: settings.cashbackRecharge,
        tag: 'Récurrent'
    },
    {
        icon: <TrophyOutlined />,
        title: `Bonus ${settings.seuilCourses}`,
        description: `Nouveaux comptes TIGOON ayant effectué ${settings.seuilCourses} en 1 semaine.`,
        highlight: settings.bonusMaxChallenge,
        tag: 'Challenge'
    },
    {
        icon: <FireOutlined />,
        title: `${settings.seuilHeures} de Course`,
        description: `Anciens et nouveaux chauffeurs atteignant ${settings.seuilHeures} de courses dans le mois.`,
        highlight: settings.bonusHeures,
        tag: 'Mensuel'
    },
    {
        icon: <CrownOutlined />,
        title: '0% Commission Partenaire',
        description: 'Profitez de 0% de commission partenaire.',
        highlight: settings.bonusCourses,
        tag: 'Premium'
    },
    {
        icon: <ThunderboltOutlined />,
        title: 'Lundi Bonus',
        description: `Premier rechargement de ${settings.montantMinLundi} le lundi.`,
        highlight: settings.bonusLundi,
        tag: 'Hebdo'
    }
];

// Valeurs par défaut pour les settings
const DEFAULT_SETTINGS = {
    bonusPremiereRecharge: '50%',
    pointsNouveau: '2 000 pts',
    pointsAncien: '1 000 pts',
    cashbackRecharge: '3%',
    bonusHeures: '2 000 pts',
    seuilHeures: '130h',
    bonusCourses: '2 000 pts',
    seuilCourses: '55 courses',
    bonusLundi: '2 000 pts',
    montantMinLundi: '10 000 F',
    bonusMaxChallenge: '4 000 pts'
};

const HomePage = () => {
    const [banners, setBanners] = useState({
        hero: [],
        middle: [],
        bottom: []
    });
    const [loading, setLoading] = useState(true);
    const [heroLoaded, setHeroLoaded] = useState(false);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [driverBenefits, setDriverBenefits] = useState(getDriverBenefits(DEFAULT_SETTINGS));

    // Hooks pour les animations au scroll
    const [featuresRef, featuresVisible] = useScrollAnimation({ threshold: 0.1 });
    const [benefitsRef, benefitsVisible] = useScrollAnimation({ threshold: 0.1 });
    const [downloadRef, downloadVisible] = useScrollAnimation({ threshold: 0.1 });

    useEffect(() => {
        fetchBanners();
        fetchSettings();
        // Animation d'entrée douce
        setTimeout(() => setHeroLoaded(true), 100);
    }, []);

    const fetchBanners = async () => {
        try {
            const response = await bannerService.getAll();
            if (response?.success && response.data) {
                setBanners(response.data);
            }
        } catch (error) {
            console.error('Error fetching banners:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await settingsService.getPublicSettings();
            if (response?.success && response.data?.display) {
                setSettings(response.data.display);
                setDriverBenefits(getDriverBenefits(response.data.display));
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    };

    // Rendu de la section hero statique (fallback)
    const renderStaticHero = () => (
        <section className={`hero-section ${heroLoaded ? 'hero-loaded' : ''}`}>
            <div className="hero-bg-pattern"></div>
            <div className="container">
                <Row gutter={[48, 48]} align="middle">
                    <Col xs={24} md={14}>
                        <div className={`hero-content ${heroLoaded ? 'animate-in' : ''}`}>
                            <div className="hero-badge animate-item" style={{ animationDelay: '0.1s' }}>
                                <RocketOutlined /> Application #1 pour chauffeurs VTC
                            </div>
                            <h1 className="animate-item" style={{ animationDelay: '0.2s' }}>
                                <span className="text-gold">TIGOON Connect.</span>
                                <br />
                                La meilleure app pour
                                <br />
                                les chauffeurs VTC.
                            </h1>
                            <p className="hero-description animate-item" style={{ animationDelay: '0.3s' }}>
                                Gérez vos courses, vos finances et restez connecté avec la communauté des chauffeurs partenaires. Profitez de bonus exclusifs !
                            </p>
                            <div className="hero-features animate-item" style={{ animationDelay: '0.4s' }}>
                                <div className="hero-feature">
                                    <CheckCircleFilled /> {settings.bonusPremiereRecharge} bonus sur 1ère recharge
                                </div>
                                <div className="hero-feature">
                                    <CheckCircleFilled /> Jusqu'à {settings.bonusMaxChallenge} offerts
                                </div>
                                <div className="hero-feature">
                                    <CheckCircleFilled /> {settings.cashbackRecharge} cashback sur recharges
                                </div>
                                <div className="hero-feature">
                                    <CheckCircleFilled /> 0% commission possible
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} md={10}>
                        <div className={`hero-phone-container ${heroLoaded ? 'animate-in' : ''}`}>
                            <div className="hero-phone-mockup">
                                <div className="phone-screen-image">
                                    <img src={AppScreenshot} alt="TIGOON Connect App" className="app-screenshot" />
                                </div>
                            </div>
                            <div className="floating-card card-1">
                                <GiftOutlined />
                                <span>+{settings.bonusPremiereRecharge} Bonus</span>
                            </div>
                            <div className="floating-card card-2">
                                <TrophyOutlined />
                                <span>{settings.bonusMaxChallenge}</span>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>
    );

    // Rendu de la section hero dynamique avec bannières
    const renderDynamicHero = () => (
        <section className="hero-section hero-banner-section">
            <div className="container">
                <Banner banners={banners.hero} type="hero" />
            </div>
        </section>
    );

    return (
        <div className="home-page">
            {/* Hero Section - Dynamique si bannières hero existent, sinon statique */}
            {!loading && banners.hero?.length > 0 ? renderDynamicHero() : renderStaticHero()}

            {/* Features Section - Nouveau Design */}
            <section
                ref={featuresRef}
                className={`features-section-new ${featuresVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    {/* Header */}
                    <div className={`features-header ${featuresVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}>
                        <span className="features-badge">Fonctionnalités</span>
                        <h2>
                            Tous les services <span className="text-gold">dans une seule appli</span>
                        </h2>
                        <p className="features-subtitle">
                            Découvrez toutes les fonctionnalités qui simplifient votre quotidien de chauffeur VTC
                        </p>
                    </div>

                    {/* Features Grid */}
                    <div className="features-grid">
                        {/* SOS Crédit */}
                        <div
                            className={`feature-card ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                            style={{ transitionDelay: '0.1s' }}
                        >
                            <div className="feature-icon-wrapper" style={{ background: 'linear-gradient(135deg, #fa8c16 0%, #ffc53d 100%)' }}>
                                <ThunderboltOutlined />
                            </div>
                            <div className="feature-content">
                                <h3>SOS Crédit</h3>
                                <p>Obtenez un prêt instantané en cas d'urgence directement depuis l'application.</p>
                            </div>
                            <div className="feature-arrow">
                                <ArrowRightOutlined />
                            </div>
                        </div>

                        {/* Transferts UV */}
                        <div
                            className={`feature-card ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                            style={{ transitionDelay: '0.15s' }}
                        >
                            <div className="feature-icon-wrapper" style={{ background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)' }}>
                                <SwapOutlined />
                            </div>
                            <div className="feature-content">
                                <h3>Transferts UV</h3>
                                <p>Transférez des unités de valeur vers d'autres chauffeurs partenaires.</p>
                            </div>
                            <div className="feature-arrow">
                                <ArrowRightOutlined />
                            </div>
                        </div>

                        {/* Classe de Service */}
                        <div
                            className={`feature-card ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                            style={{ transitionDelay: '0.2s' }}
                        >
                            <div className="feature-icon-wrapper" style={{ background: 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)' }}>
                                <SettingOutlined />
                            </div>
                            <div className="feature-content">
                                <h3>Classe de Service</h3>
                                <p>Changez la classe de service de votre véhicule selon vos besoins.</p>
                            </div>
                            <div className="feature-arrow">
                                <ArrowRightOutlined />
                            </div>
                        </div>

                        {/* Ajout de Véhicule */}
                        <div
                            className={`feature-card ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                            style={{ transitionDelay: '0.25s' }}
                        >
                            <div className="feature-icon-wrapper" style={{ background: 'linear-gradient(135deg, #13c2c2 0%, #5cdbd3 100%)' }}>
                                <CarOutlined />
                            </div>
                            <div className="feature-content">
                                <h3>Ajout de Véhicule</h3>
                                <p>Ajoutez facilement un nouveau véhicule à votre compte chauffeur.</p>
                            </div>
                            <div className="feature-arrow">
                                <ArrowRightOutlined />
                            </div>
                        </div>

                        {/* Se Lier à un Véhicule */}
                        <div
                            className={`feature-card ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                            style={{ transitionDelay: '0.3s' }}
                        >
                            <div className="feature-icon-wrapper" style={{ background: 'linear-gradient(135deg, #722ed1 0%, #b37feb 100%)' }}>
                                <LinkOutlined />
                            </div>
                            <div className="feature-content">
                                <h3>Se Lier à un Véhicule</h3>
                                <p>Associez-vous à un véhicule existant pour commencer à travailler.</p>
                            </div>
                            <div className="feature-arrow">
                                <ArrowRightOutlined />
                            </div>
                        </div>

                        {/* Se Détacher */}
                        <div
                            className={`feature-card ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                            style={{ transitionDelay: '0.35s' }}
                        >
                            <div className="feature-icon-wrapper" style={{ background: 'linear-gradient(135deg, #eb2f96 0%, #ff85c0 100%)' }}>
                                <DisconnectOutlined />
                            </div>
                            <div className="feature-content">
                                <h3>Se Détacher</h3>
                                <p>Détachez-vous d'un véhicule en toute simplicité quand vous le souhaitez.</p>
                            </div>
                            <div className="feature-arrow">
                                <ArrowRightOutlined />
                            </div>
                        </div>

                        {/* Changement de Numéro */}
                        <div
                            className={`feature-card ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                            style={{ transitionDelay: '0.4s' }}
                        >
                            <div className="feature-icon-wrapper" style={{ background: 'linear-gradient(135deg, #2f54eb 0%, #85a5ff 100%)' }}>
                                <PhoneOutlined />
                            </div>
                            <div className="feature-content">
                                <h3>Changement de Numéro</h3>
                                <p>Changez votre numéro de téléphone sans perdre vos données.</p>
                            </div>
                            <div className="feature-arrow">
                                <ArrowRightOutlined />
                            </div>
                        </div>

                        {/* Assistance 24/7 */}
                        <div
                            className={`feature-card ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                            style={{ transitionDelay: '0.45s' }}
                        >
                            <div className="feature-icon-wrapper" style={{ background: 'linear-gradient(135deg, #D4A853 0%, #f5d89a 100%)' }}>
                                <CustomerServiceOutlined />
                            </div>
                            <div className="feature-content">
                                <h3>Assistance 24/7</h3>
                                <p>Notre équipe est disponible à tout moment pour vous aider.</p>
                            </div>
                            <div className="feature-arrow">
                                <ArrowRightOutlined />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Driver Benefits Section */}
            <section
                ref={benefitsRef}
                className={`home-benefits-section ${benefitsVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`benefits-header ${benefitsVisible ? 'animate-fade-down is-visible' : 'animate-fade-down'}`}>
                        <h2>
                            <span className="text-gold">Gagnez plus</span> avec TIGOON Connect
                        </h2>
                        <p>Découvrez tous les avantages exclusifs réservés à nos chauffeurs partenaires</p>
                    </div>
                    <Row gutter={[24, 24]}>
                        {driverBenefits.map((benefit, index) => (
                            <Col xs={24} sm={12} md={6} key={index}>
                                <div
                                    className={`benefit-card ${benefitsVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                                    style={{ transitionDelay: `${0.1 + index * 0.1}s` }}
                                >
                                    <div className="benefit-tag">{benefit.tag}</div>
                                    <div className="benefit-icon">{benefit.icon}</div>
                                    <div className="benefit-highlight">{benefit.highlight}</div>
                                    <h3 className="benefit-title">{benefit.title}</h3>
                                    <p className="benefit-description">{benefit.description}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* Middle Banners */}
            {!loading && banners.middle?.length > 0 && (
                <section className="banners-section">
                    <div className="container">
                        <Banner banners={banners.middle} type="middle" />
                    </div>
                </section>
            )}

            {/* Download Section */}
            <section
                ref={downloadRef}
                className={`download-section ${downloadVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <Row gutter={[48, 48]} align="middle">
                        <Col xs={24} md={12}>
                            <div className={`download-content ${downloadVisible ? 'animate-fade-right is-visible' : 'animate-fade-right'}`}>
                                <h2>Téléchargez l'application</h2>
                                <p>
                                    Rejoignez la communauté TIGOON Connect et profitez de tous
                                    les avantages offerts aux chauffeurs VTC partenaires.
                                </p>
                                <ul className="download-features">
                                    <li className="animate-list-item" style={{ animationDelay: '0.1s' }}>Gestion simplifiée de vos courses</li>
                                    <li className="animate-list-item" style={{ animationDelay: '0.2s' }}>Transferts et SOS Crédit</li>
                                    <li className="animate-list-item" style={{ animationDelay: '0.3s' }}>Promotions exclusives</li>
                                    <li className="animate-list-item" style={{ animationDelay: '0.4s' }}>Assistance 24/7</li>
                                </ul>
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className={`phone-mockup ${downloadVisible ? 'animate-fade-left is-visible' : 'animate-fade-left'}`}>
                                <div className="phone-frame animate-float">
                                    <div className="phone-screen-image">
                                        <img src={AppScreenshot} alt="TIGOON Connect App" className="app-screenshot" />
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </section>

            {/* Bottom Banners */}
            {!loading && banners.bottom?.length > 0 && (
                <section className="banners-section bottom-banners">
                    <div className="container">
                        <Banner banners={banners.bottom} type="bottom" />
                    </div>
                </section>
            )}

            {/* Fixed Download Bar */}
            <div className="fixed-download-bar">
                <div className="download-bar-card">
                    <span className="download-bar-text">
                        Téléchargez l'application TIGOON Connect
                    </span>
                    <div className="download-bar-badges">
                        <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="store-badge-small">
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                                alt="Google Play"
                                height="40"
                            />
                        </a>
                        {APP_STORE_AVAILABLE ? (
                            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="store-badge-small">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                                    alt="App Store"
                                    height="40"
                                />
                            </a>
                        ) : (
                            <div className="store-badge-coming-soon">
                                <AppleOutlined />
                                <span>Bientôt disponible</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
